/**
 * PhoneQRModal.jsx
 * v433.10: Standardized Ghost modal branding
 * "Call Now" triggers Twilio click-to-call
 * QR code kept for scan-to-call from mobile
 */
import { useState, useEffect } from 'react';
import { Copy, Check, QrCode, Loader2, PhoneOutgoing } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import GhostModalHeader from './GhostModalHeader';
// v3.21-1612 · CallOutcomeModal retired — ActiveCallPill is SSOT for
// disposition + notes + End Call.
import { SectionErrorBoundary } from './SectionErrorBoundary';
import { canUsePhoneSystem, PHONE_UNAUTHORIZED_MESSAGE, canUseIntegratedPBX } from '../lib/phone_access';
// v3.21-1634 · P0 client-local-time late-call safeguard
import useOutboundCall from '../hooks/useOutboundCall';
import LateCallWarningModal from './LateCallWarningModal';

const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

const formatPhoneDisplay = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

const generateQRCodeUrl = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  const telUrl = `tel:${cleaned}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(telUrl)}`;
};

const useClickToCall = () => {
  // v3.21-1634 · Delegates to the shared useOutboundCall hook so every
  // outbound-call surface runs through the client-local-time preflight.
  const {
    calling, initiateOutboundCall, preflight, proceedLateCall,
    cancelLateCall, lateCallWarningOpen,
  } = useOutboundCall();

  const initiateCall = (phone, { clientId, clientName, callAs } = {}) =>
    initiateOutboundCall(phone, { clientId, clientName, callAs,
                                  context: 'phone_qr_modal' });

  return { calling, initiateCall, preflight, proceedLateCall,
           cancelLateCall, lateCallWarningOpen };
};

/* Shared modal body */
const ModalBody = ({ phone, name, calling, onCall, onCopy, copied, callAs, setCallAs, pbxEntitled }) => {
  const formattedPhone = formatPhoneDisplay(phone);
  const qrCodeUrl = generateQRCodeUrl(phone);

  return (
    <div className="flex flex-col items-center px-6 py-5 space-y-5">
      <div className="text-center">
        <p className="text-2xl font-bold text-slate-900 tracking-wide">{formattedPhone}</p>
        {name && <p className="text-sm text-slate-500 mt-1">{name}</p>}
      </div>

      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
        <img src={qrCodeUrl} alt={`QR code for ${formattedPhone}`} className="w-40 h-40" data-testid="phone-qr-code" />
      </div>

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <QrCode className="h-3.5 w-3.5" />
        Scan with your phone camera to call
      </p>

      {/* v3.19-1403 CRM-SEC-001 — "Call As" selector and "Call Now" click-to-call
          button are integrated-PBX features.  Only render for users whose
          email domain is on the PBX entitlement allow-list.  Unentitled
          companies see QR + Copy only (the QR workflow remains unchanged). */}
      {pbxEntitled && (
        <div className="w-full">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Call As</label>
          <Select value={callAs} onValueChange={setCallAs}>
            <SelectTrigger className="w-full h-9 text-sm" data-testid="call-as-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* v3.21-1794 · Show the actual originating outbound
                  number next to each label so operators immediately
                  know which line they'll be calling from.  Labels are
                  DISPLAY only — the underlying line-id value
                  (`eqf_hub` / `ptc_travel`) is unchanged, so call
                  routing is preserved.  Numbers must stay in sync
                  with backend env `TWILIO_PHONE_NUMBER` (Line 1) and
                  `TWILIO_PHONE_NUMBER_2` (Line 2). */}
              <SelectItem value="eqf_hub">EasyQuickFund / PTC Hub — (757) 448-3705</SelectItem>
              <SelectItem value="ptc_travel">Prestigious Travel Club — (407) 305-7738</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex gap-3 w-full">
        {pbxEntitled && (
          <Button
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white h-10"
            onClick={onCall}
            disabled={calling}
            data-testid="call-now-btn"
          >
            {calling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneOutgoing className="h-4 w-4 mr-2" />}
            {calling ? 'Calling...' : 'Call Now'}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onCopy}
          className={pbxEntitled ? "flex-1 h-10" : "w-full h-10"}
          data-testid="copy-number-btn"
        >
          {copied ? <><Check className="h-4 w-4 mr-2 text-green-600" />Copied!</> : <><Copy className="h-4 w-4 mr-2" />Copy</>}
        </Button>
      </div>
    </div>
  );
};

/**
 * PhoneClickable - Clickable phone that opens Ghost-branded call modal.
 *
 * v3.21-1795 · `disableModal` behaviour tightened.  When true (every
 * active Client workflow — Client Workspace / Deal Dashboard /
 * Client Detail Client Info / Client Quick View), clicking the
 * phone number NO LONGER places a call directly.  It dispatches a
 * global `phone-click:open-workspace` event with the Client id +
 * focus target so the operator is routed into the integrated
 * Client Workspace CALL panel:
 *   • IF Client Workspace is already open   → focus/scroll/highlight
 *     the CALL panel (handler in ClientWorkspaceModal).
 *   • IF Client Workspace is NOT open       → the workspace context
 *     provider opens the workspace for that Client, then the modal
 *     mounts and its own listener focuses the CALL panel.
 * The canonical `useOutboundCall` hook is invoked ONLY by the
 * "Call Now" button inside the integrated CALL panel — the operator
 * always sees Call As + originating outbound number before the call
 * is placed.
 *
 * Non-Client legitimate surfaces (Users directory, Ghost
 * Appointments, unresolved unknown callers) continue to omit
 * `disableModal` and keep the shared modal utility for scan-to-call.
 */
export const PhoneClickable = ({
  phone, name, clientId, clientName, className = '', showIcon = true,
  disableModal = false,   // v3.21-1794 · suppress popup for Client workflows
  callAs: callAsProp = 'eqf_hub',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [callAs, setCallAs] = useState(callAsProp);
  const { calling, initiateCall, preflight, proceedLateCall,
          cancelLateCall, lateCallWarningOpen } = useClickToCall();
  const { user } = useAuth();
  const pbxEntitled = canUseIntegratedPBX(user);  // v3.19-1403 CRM-SEC-001

  if (!phone) return <span className="text-slate-400">No phone</span>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cleanPhoneNumber(phone));
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  // v3.21-1795 · Client-workflow phone-click path.  Renders the same
  // clickable phone text but dispatches a global event that routes
  // the operator into the integrated Client Workspace CALL panel.
  // NO outbound call is placed and NO popup is mounted here — only
  // "Call Now" inside the workspace CALL panel initiates a call.
  if (disableModal) {
    return (
      <button
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          try {
            window.dispatchEvent(new CustomEvent('phone-click:open-workspace', {
              detail: {
                clientId: clientId || null,
                clientName: clientName || name || null,
                focus: 'call',
              },
            }));
          } catch (_) {}
        }}
        className={`inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer ${className}`}
        data-testid="phone-clickable-workspace"
        data-phone-clickable-workspace="true"
        title="Open Client Workspace to review Call As line before dialling"
      >
        {showIcon && <PhoneOutgoing className="h-3.5 w-3.5" />}
        <span>{formatPhoneDisplay(phone)}</span>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(true); }}
        className={`inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer ${className}`}
        data-testid="phone-clickable"
      >
        {showIcon && <PhoneOutgoing className="h-3.5 w-3.5" />}
        <span>{formatPhoneDisplay(phone)}</span>
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden rounded-2xl shadow-2xl border-0" data-testid="phone-qr-modal">
          <DialogTitle className="sr-only">{name ? `Call ${name}` : 'Place Call'}</DialogTitle>
          <DialogDescription className="sr-only">Place a call or scan the QR code to dial from a mobile device.</DialogDescription>
          <SectionErrorBoundary label="Phone Call" testId="phone-qr-modal-error">
            <GhostModalHeader subtitle={name ? `Call ${name}` : 'Place Call'} />
            <ModalBody
              phone={phone} name={name} calling={calling} copied={copied}
              callAs={callAs} setCallAs={setCallAs}
              pbxEntitled={pbxEntitled}
              onCall={() => initiateCall(phone, { clientId, clientName: clientName || name, callAs })}
              onCopy={handleCopy}
            />
          </SectionErrorBoundary>
        </DialogContent>
      </Dialog>

      {/* v3.21-1612 · CallOutcomeModal removed — ActiveCallPill handles
          disposition, notes, and End Call for every outbound Twilio call. */}
      {/* v3.21-1634 · Late-call warning safeguard */}
      <LateCallWarningModal
        open={lateCallWarningOpen}
        preflight={preflight}
        onCancel={cancelLateCall}
        onProceed={proceedLateCall}
        proceeding={calling}
      />
    </>
  );
};

/**
 * PhoneQRModal - Standalone modal
 */
const PhoneQRModal = ({ phone, name, clientId, clientName, isOpen, onClose, trigger }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [callAs, setCallAs] = useState('eqf_hub');
  const { calling, initiateCall, preflight, proceedLateCall,
          cancelLateCall, lateCallWarningOpen } = useClickToCall();

  const open = isOpen !== undefined ? isOpen : modalOpen;
  const setOpen = onClose !== undefined ? (val) => !val && onClose() : setModalOpen;

  if (!phone) return trigger || null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cleanPhoneNumber(phone));
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  return (
    <>
      {trigger && <div onClick={() => setOpen(true)} className="cursor-pointer">{trigger}</div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden rounded-2xl shadow-2xl border-0" data-testid="phone-qr-modal">
          <DialogTitle className="sr-only">{name ? `Call ${name}` : 'Place Call'}</DialogTitle>
          <DialogDescription className="sr-only">Place a call or scan the QR code to dial from a mobile device.</DialogDescription>
          <SectionErrorBoundary label="Phone Call" testId="phone-qr-modal-error-2">
            <GhostModalHeader subtitle={name ? `Call ${name}` : 'Place Call'} />
            <ModalBody
              phone={phone} name={name} calling={calling} copied={copied}
              callAs={callAs} setCallAs={setCallAs}
              onCall={() => initiateCall(phone, { clientId, clientName: clientName || name, callAs })}
              onCopy={handleCopy}
            />
          </SectionErrorBoundary>
        </DialogContent>
      </Dialog>

      {/* v3.21-1612 · CallOutcomeModal removed — ActiveCallPill SSOT. */}
      {/* v3.21-1634 · Late-call warning safeguard */}
      <LateCallWarningModal
        open={lateCallWarningOpen}
        preflight={preflight}
        onCancel={cancelLateCall}
        onProceed={proceedLateCall}
        proceeding={calling}
      />
    </>
  );
};

export default PhoneQRModal;
